/**
 * Collection Filter Component
 *
 * Main controller for the collection filtering system. Coordinates AJAX filtering,
 * hierarchy management, and mobile/desktop synchronization.
 *
 * @component collection-filter
 * @extends HTMLElement
 *
 * @description
 * This is the root component for the entire filter system. It manages:
 * - Filter state and parent-child relationships
 * - AJAX requests for product grid updates
 * - URL parameter management and browser history
 * - Mobile dialog and desktop dropdown synchronization via portal components
 * - Availability filtering (hiding out-of-stock sizes)
 *
 * @architecture
 * Component Hierarchy:
 * ```
 * <collection-filter> (this component)
 *   ├── <dialog-component#filter-modal> (mobile)
 *   │   └── Filter content with portal:source elements
 *   ├── <div data-filter-bar> (desktop)
 *   │   └── <dropdown-component> per filter
 *   │       └── <portal-component> (consumes portal:source)
 *   └── <div data-output="collection-grid"> (results)
 *       └── <loading-overlay>
 * ```
 *
 * @dataflow
 * Filter Change Flow:
 * 1. User clicks checkbox → filter-item dispatches 'filter:change'
 * 2. This component debounces (50ms) and calls applyFilters()
 * 3. buildFilterFormData() collapses hierarchy (parent + all children = parent only)
 * 4. AJAX fetch with filter params
 * 5. replaceOutputs() swaps all [data-output] elements
 * 6. Dispatches 'portal:recall' to sync mobile/desktop content
 * 7. getAllFilters() rebuilds parent-child Map
 * 8. Browser history updated with new URL
 *
 * @hierarchy_collapsing
 * Algorithm to optimize URL parameters:
 * - If parent is checked AND all children are checked → send only parent
 * - If parent is checked AND some children are checked → send unchecked children
 * - This prevents redundant filter params in URL
 *
 * Example:
 * - Parent: "Clothing" (ID: gid://123)
 * - Children: "Tops" (ID: gid://124), "Bottoms" (ID: gid://125)
 * - If all 3 are checked → URL only has filter.v.department=gid://123
 * - If only Clothing + Tops → URL has filter.v.department=gid://123&filter.v.category=gid://125
 *
 * @silent_mode
 * When mobile dialog is open, checkbox changes set #pendingFilterChange flag
 * but don't trigger AJAX. This allows users to make multiple changes, then click
 * "Apply" to fetch once. Closing dialog without applying restores checkboxes from URL.
 *
 * @attributes
 * @attr {string} collection-url - Base collection URL for AJAX requests
 * @attr {string} section-id - Shopify section ID for section rendering
 * @attr {string} department-param - Name of department filter param (from metafield)
 * @attr {string} category-param - Name of category filter param (from metafield)
 * @attr {string} size-param - Name of size filter param (from metafield)
 *
 * @fires filter:change - Bubbles up from child components when filters change
 * @fires filter:clear - Dispatched by filter-clear component
 * @fires portal:recall - Dispatched to trigger mobile/desktop sync
 * @fires loading:start - Dispatched to show loading overlay
 * @fires loading:end - Dispatched to hide loading overlay
 *
 * @listens filter:change - Triggers debounced applyFilters()
 * @listens filter:clear - Unchecks all filters and re-fetches
 * @listens portal:recall - Rebuilds filter Map after DOM changes
 * @listens dialog:open - Enables silent mode (mobile)
 * @listens dialog:close - Restores filters if not applied (mobile)
 *
 * @example
 * ```liquid
 * <collection-filter
 *   collection-url="{{ collection.url }}"
 *   section-id="{{ section.id }}"
 *   department-param="{{ shop.metafields.filters.department_param.value }}"
 *   category-param="{{ shop.metafields.filters.category_param.value }}"
 *   size-param="{{ shop.metafields.filters.size_param.value }}"
 * >
 *   <!-- Filter UI (dialog, dropdowns, product grid) -->
 * </collection-filter>
 * ```
 *
 * @extension_points
 * To add a new filter type:
 * 1. Add metafield param in Shopify admin
 * 2. Pass param as attribute to <collection-filter>
 * 3. Create Liquid snippet in snippets/filter-{type}.liquid
 * 4. Add case in snippets/filter-mobile.liquid to route to your snippet
 * 5. Filter will automatically work with hierarchy collapsing if nested
 *
 * @dependencies
 * - filter-item.js - Handles checkbox parent/child coordination
 * - filter-count.js - Displays active filter counts
 * - filter-clear.js - Clear all button
 * - filter-apply.js - Mobile apply button
 * - portal.js - Teleports content between mobile/desktop
 * - dropdown.js - Desktop dropdown behavior
 * - dialog.js - Mobile modal dialog
 * - loading-overlay.js - Loading spinner
 */
class CollectionFilter extends HTMLElement {
  #abortController = null;
  #debounceTimer = null;
  #filters = new Map();
  #silentMode = false;
  #pendingFilterChange = false;
  #filterDialog = null;
  #filtersApplied = false;

  connectedCallback() {
    // Store section reference
    this.section = this.closest('.shopify-section');

    // Store dialog reference (scoped to this section)
    this.#filterDialog = this.section?.querySelector('#filter-modal');

    // Listen for dialog events to toggle silent mode
    if (this.#filterDialog) {
      this.#filterDialog.addEventListener('dialog:open', () => {
        this.#silentMode = true;
        this.#pendingFilterChange = false;
        this.#filtersApplied = false;
      });

      this.#filterDialog.addEventListener('dialog:close', () => {
        this.#silentMode = false;

        // If filters weren't applied, restore checkboxes to match URL
        if (!this.#filtersApplied) {
          this.restoreFiltersFromURL();
        }
      });
    }

    // Get configuration from attributes
    this.collectionUrl = this.getAttribute('collection-url');
    this.sectionId = this.getAttribute('section-id');
    this.departmentParam = this.getAttribute('department-param');
    this.categoryParam = this.getAttribute('category-param');
    this.sizeParam = this.getAttribute('size-param');

    // Build initial filter map (parent-child relationships)
    this.#filters = this.getAllFilters(this.querySelectorAll('input[name^="filter."]'));

    // Mirror the filter groups' collapsed state onto aria-expanded. The accordions
    // in blocks/_filter.liquid are pure CSS (a hidden checkbox + grid-template-rows),
    // and CSS can't set an ARIA attribute — so one delegated listener covers every
    // group, including any rendered by a later AJAX update.
    this.addEventListener('change', (e) => {
      const toggle = e.target;
      if (toggle instanceof HTMLInputElement && toggle.hasAttribute('data-filter-accordion')) {
        toggle.setAttribute('aria-expanded', String(toggle.checked));
      }
    });

    // Listen for filter change events
    this.addEventListener('filter:change', (e) => this.handleFilterChange(e), { capture: true });
    this.addEventListener('filter:clear', (e) => this.handleClearFilters(e), { capture: true });

    // Bind portal recall handler for proper cleanup
    this.boundHandleRecall = () => this.handleRecall();
    document.addEventListener('portal:recall', this.boundHandleRecall);

    // Initialize availability filter (hide sold-out sizes)
    this.initializeAvailabilityFilter();

    // Initialize sort listeners
    this.initializeSortListeners();
  }

  /**
   * Initialize event listeners for sort radio inputs
   * Called on initial load and after each AJAX update
   */
  initializeSortListeners() {
    const sortInputs = this.querySelectorAll('input[name="sort_by"]');

    sortInputs.forEach((radio) => {
      radio.addEventListener('change', () => {
        // Close dropdown
        const dropdown = radio.closest('dropdown-component');
        if (dropdown) {
          dropdown.dispatchEvent(
            new CustomEvent('dropdown:toggle', {
              detail: { el: dropdown, open: false },
              bubbles: true,
              composed: true,
            })
          );
        }

        // Trigger filter change
        this.dispatchEvent(
          new CustomEvent('filter:change', {
            bubbles: true,
          })
        );
      });
    });
  }

  /**
   * Builds parent-child relationship map for all filter inputs.
   *
   * @description
   * Traverses the DOM to find hierarchical relationships between filters.
   * Each filter input is added to the Map, keyed by its VALUE (metaobject ID).
   * If an input is nested inside another filter's [data-param-name], it's
   * registered as a child of that parent filter.
   *
   * @param {NodeList} inputs - All filter input elements
   * @returns {Map<string, FilterItem>} Map of filter items keyed by value
   *
   * @typedef {Object} FilterItem
   * @property {string} param - Filter parameter name (e.g., "filter.v.option.Department")
   * @property {string[]} children - Array of child filter values
   * @property {string} value - Filter value (metaobject ID)
   * @property {HTMLInputElement} input - Reference to the input element
   *
   * @example
   * // DOM structure:
   * // <div data-param-name="filter.v.option.Department">
   * //   <input name="filter.v.option.Department" value="gid://123" /> <!-- Parent -->
   * //   <div data-param-name="filter.v.option.Category">
   * //     <input name="filter.v.option.Category" value="gid://456" /> <!-- Child -->
   * //   </div>
   * // </div>
   * //
   * // Resulting Map:
   * // {
   * //   "gid://123": { param: "filter.v.option.Department", children: ["gid://456"], value: "gid://123", input: HTMLInputElement },
   * //   "gid://456": { param: "filter.v.option.Category", children: [], value: "gid://456", input: HTMLInputElement }
   * // }
   */
  getAllFilters(inputs) {
    return Array.from(inputs).reduce((map, input) => {
      // Find parent input (if exists)
      const parentInput = input?.closest(`[data-param-name]:not([data-param-name="${input.name}"])`)?.querySelector(`input[name^="filter."]:not([name="${input.name}"])`);

      // If this input has a parent, update parent's children array
      if (parentInput) {
        const parent = map.get(parentInput.value) ?? {
          param: parentInput.name,
          children: [],
          value: parentInput.value,
          input: parentInput,
        };

        parent.children.push(input.value);
        map.set(parentInput.value, { ...parent });
      }

      // Add this input to map if not already present
      if (!map.has(input.value)) {
        map.set(input.value, {
          param: input.name,
          children: [],
          value: input.value,
          input,
        });
      }

      return map;
    }, new Map());
  }

  /**
   * Public getter for filters Map
   */
  get filters() {
    return this.#filters;
  }

  /**
   * Debounced filter change handler (50ms like source)
   */
  handleFilterChange(event) {
    // If dialog is open (silent mode), just mark as pending
    if (this.#silentMode) {
      this.#pendingFilterChange = true;
      return;
    }

    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.applyFilters(), 50);
  }

  /**
   * Clear all filters
   */
  handleClearFilters(event) {
    event.stopPropagation();

    // Uncheck all filter inputs
    this.querySelectorAll('input[name^="filter."]').forEach((input) => {
      input.checked = false;
    });

    // Reset the price slider back to its full range (not a checkbox).
    this.querySelector('[data-price-filter]')?.reset?.();

    this.dispatchEvent(new CustomEvent('filter:change'));
  }

  /**
   * Handle portal recall event (for mobile/desktop sync)
   */
  handleRecall() {
    // Rebuild filter map when portal recalls DOM
    this.#filters = this.getAllFilters(this.querySelectorAll('input[name^="filter."]'));
  }

  /**
   * Initialize availability filter on page load
   * Fetches collection with availability=1 filter and caches in sessionStorage
   */
  async initializeAvailabilityFilter() {
    // Skip if availability filter already in URL (already fetched by server)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('filter.v.availability')) {
      return;
    }

    // Extract collection handle from URL for cache key
    const collectionHandle = this.collectionUrl.split('/').filter(Boolean).pop();
    const cacheKey = `collection-availability-${collectionHandle}`;

    // Check sessionStorage for cached data
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      // Use cached data
      this.replaceAvailabilityFilter(cached);
      return;
    }

    // Build URL for availability-filtered collection
    const url = new URL(this.collectionUrl, window.location.origin);

    url.searchParams.set('section_id', 'api-filter');
    url.searchParams.set('filter.v.availability', '1');

    // Add currently active size filters to preserve selections
    this.#filters.forEach((item) => {
      if (item.input.checked && item.param === this.sizeParam) {
        url.searchParams.append(item.param, item.value);
      }
    });

    try {
      const response = await fetch(url.toString());

      if (response.ok) {
        const html = await response.text();

        // Cache the response
        sessionStorage.setItem(cacheKey, html);

        // Replace the size filter UI
        this.replaceAvailabilityFilter(html);
      } else {
        console.error('Availability filter failed:', response.status);
        this.showErrorMessage('Unable to load available sizes.');
      }
    } catch (error) {
      console.error('Availability filter error:', error);
      this.showErrorMessage('Unable to load available sizes.');
    }
  }

  /**
   * Replace size filter UI with availability-filtered version
   */
  replaceAvailabilityFilter(htmlString) {
    const sizeFilterOutput = this.section?.querySelector('[data-output="size_filter"]');

    if (!sizeFilterOutput) return;

    // Parse response HTML
    const html = new DOMParser().parseFromString(htmlString, 'text/html').querySelector('.shopify-section');

    if (!html) return;

    // Find the size filter in response
    const newSizeFilter = html.querySelector('[data-output="size_filter"]');

    if (!newSizeFilter) {
      return;
    }

    // Replace with outerHTML
    sizeFilterOutput.outerHTML = newSizeFilter.outerHTML;

    // Emit portal recall to sync mobile/desktop
    this.dispatchEvent(
      new CustomEvent('portal:recall', {
        bubbles: true,
      })
    );

    // Rebuild filter map with new DOM
    this.#filters = this.getAllFilters(this.querySelectorAll('input[name^="filter."]'));
  }

  /**
   * Apply filters and close dialog (for mobile filter modal)
   */
  applyFiltersAndClose() {
    this.#silentMode = false;
    this.#filtersApplied = true;

    if (this.#filterDialog?.closeDialog) {
      this.#filterDialog.closeDialog();
    }

    window.requestAnimationFrame(() => {
      if (this.#pendingFilterChange) {
        this.#pendingFilterChange = false;
        this.applyFilters();
      }
    });
  }

  /**
   * Applies filters via AJAX and updates DOM
   * EXACT pattern from source implementation
   */
  async applyFilters() {
    // Cancel previous request
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Show loading overlay
    document.dispatchEvent(
      new CustomEvent('loading:start', {
        detail: { id: this.sectionId },
        bubbles: true,
      })
    );

    // Set section loading state
    this.section.ariaBusy = 'true';

    // Build FormData with hierarchy collapsing logic
    const formData = this.buildFilterFormData();

    // Build URL with filter params
    const url = new URL(window.location.href);
    url.search = '';

    for (const [param, value] of formData.entries()) {
      url.searchParams.append(param, value);
    }

    // Add section_id for Shopify section rendering
    url.searchParams.append('section_id', this.sectionId);

    // Scroll to filter bar if user has scrolled significantly past it
    const filterBar = this.section?.querySelector('[data-filter-bar]');
    const filterBarSibling = this.section?.querySelector('[data-filter-bar-sibling]');

    if (filterBar) {
      let filterBarDocumentTop = (filterBarSibling ?? filterBar).getBoundingClientRect().top + window.scrollY;
      let filterBarHeight = filterBar.offsetHeight;

      const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      const currentScrollPosition = window.scrollY;

      // Calculate target scroll position
      const targetScrollPosition = filterBarDocumentTop - filterBarHeight - headerHeight;
      const scrolledPastFilterBar = currentScrollPosition - targetScrollPosition;

      // Only scroll if user is more than 100px past the filter bar
      if (scrolledPastFilterBar > 100) {
        window.scrollTo({
          top: targetScrollPosition,
          behavior: 'smooth',
        });
      }
    }

    try {
      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
      });

      if (response.ok) {
        const html = await response.text();

        // Emit portal recall (sync mobile/desktop)
        this.dispatchEvent(
          new CustomEvent('portal:recall', {
            bubbles: true,
          })
        );

        // Replace all [data-output] elements
        this.replaceOutputs(html);

        // Rebuild filter map with new DOM
        this.#filters = this.getAllFilters(this.querySelectorAll('input[name^="filter."]'));

        // Update browser history (remove section_id)
        url.searchParams.delete('section_id');
        history.replaceState(null, '', url.toString());

        // Hide loading overlay
        document.dispatchEvent(
          new CustomEvent('loading:end', {
            detail: { id: this.sectionId },
            bubbles: true,
          })
        );

        // Clear section loading state
        this.section.ariaBusy = 'false';
      }
    } catch (error) {
      // Only hide loading overlay for non-abort errors
      if (error.name !== 'AbortError') {
        console.error('Filter error:', error);

        // Hide loading overlay on real errors
        document.dispatchEvent(
          new CustomEvent('loading:end', {
            detail: { id: this.sectionId },
            bubbles: true,
          })
        );

        // Clear section loading state
        this.section.ariaBusy = 'false';

        // Show user-facing error message
        this.showErrorMessage('Unable to load filters. Please try again.');
      }
      // For AbortError: do nothing - the new request will handle loading state
    }
  }

  /**
   * Builds FormData with hierarchy collapsing logic.
   *
   * @description
   * Converts checked filter inputs to FormData, but applies hierarchy collapsing:
   * - If parent + ALL children are checked → send only parent
   * - If parent + SOME children are checked → send parent + unchecked children
   * - This optimizes URL params and matches Shopify's filter interpretation
   *
   * @returns {FormData} Form data with collapsed hierarchy
   *
   * @example
   * // Before collapse:
   * // Department: "Clothing" (checked)
   * // Categories: "Tops" (checked), "Bottoms" (checked)
   * //
   * // After collapse:
   * // FormData only has: filter.v.option.Department=gid://123
   * // (Children removed because all were selected)
   *
   * @example
   * // Before collapse:
   * // Department: "Clothing" (checked)
   * // Categories: "Tops" (checked), "Bottoms" (unchecked)
   * //
   * // After collapse:
   * // FormData has: filter.v.option.Department=gid://123&filter.v.option.Category=gid://125
   * // (Sends parent + the UNCHECKED child, effectively filtering to parent - unchecked)
   */
  buildFilterFormData() {
    const formData = new FormData();

    this.#filters.forEach((item) => {
      if (item.input.checked) {
        formData.append(item.param, item.value);
      }
    });

    const categoryParams = [this.departmentParam, this.categoryParam];
    const categoryIds = categoryParams.filter((p) => formData.has(p));

    /**
     * @type {Map<string, FilterItem>}
     */
    const values = new Map([...formData.values().map((i) => [i, this.#filters.get(i)])]);

    for (const categoryId of categoryIds) {
      const selectedCategories = formData.getAll(categoryId);
      let shouldParseChildren = false;

      for (const itemId of selectedCategories) {
        const item = this.#filters.get(itemId);
        const hasSelectedChildren = item.children.some((i) => values.has(i));
        const allChildrenSelected = item.children.length > 0 && item.children.every((i) => values.has(i));

        if (hasSelectedChildren && !allChildrenSelected) {
          shouldParseChildren = true;
          break;
        } else if (allChildrenSelected) {
          const childParam = this.#filters.get(item.children[0]).param;
          const currentEntries = formData.getAll(childParam);

          formData.delete(childParam);

          for (const child of item.children) {
            values.delete(child);
          }

          currentEntries.filter((entry) => !item.children.includes(entry)).forEach((entry) => formData.append(childParam, entry));
        }
      }

      if (shouldParseChildren) {
        for (const itemId of selectedCategories) {
          const item = this.#filters.get(itemId);
          const hasSelectedChildren = item.children.some((i) => values.get(i));

          if (hasSelectedChildren) {
            continue;
          }

          for (const childId of item.children) {
            const child = this.#filters.get(childId);

            formData.append(child.param, childId);
            values.set(childId, child);
          }
        }
      }
    }

    // Price range slider (not a checkbox — read straight off the component).
    // Values are kept in currency subunits; Shopify's gte/lte want major units.
    const price = this.querySelector('[data-price-filter]');
    if (price) {
      const min = Number(price.dataset.min);
      const max = Number(price.dataset.max);
      const valueMin = Number(price.dataset.valueMin);
      const valueMax = Number(price.dataset.valueMax);

      if (Number.isFinite(valueMin) && valueMin > min && price.dataset.gteName) {
        formData.append(price.dataset.gteName, Math.round(valueMin / 100));
      }
      if (Number.isFinite(valueMax) && valueMax < max && price.dataset.lteName) {
        formData.append(price.dataset.lteName, Math.round(valueMax / 100));
      }
    }

    // ALWAYS include availability filter on every request
    // This ensures API always returns only available sizes
    formData.set('filter.v.availability', '1');

    // Add sort_by if present (query checked radio dynamically)
    const selectedSort = this.querySelector('input[name="sort_by"]:checked');
    if (selectedSort?.value) {
      formData.append('sort_by', selectedSort.value);
    }

    return formData;
  }

  /**
   * Replaces all [data-output] elements with API response
   * API always includes filter.v.availability, so no special handling needed
   */
  replaceOutputs(htmlString) {
    const outputs = this.section?.querySelectorAll('[data-output]');
    if (!outputs || outputs.length === 0) return;

    const html = new DOMParser().parseFromString(htmlString, 'text/html').querySelector('.shopify-section');
    if (!html) return;

    // Replace all outputs with API response
    outputs.forEach((output) => {
      const target = html.querySelector(`[data-output="${output.dataset.output}"]`);

      // Fail-safe: if the expected fragment isn't in the response (e.g. an in-app
      // browser served a cached full page), keep the existing content rather than
      // wiping it — never leave the shopper with an empty grid.
      if (!target) return;

      // Move the already-parsed nodes in instead of re-serialising via outerHTML.
      // replaceChildren tears the old grid down before attaching the new one, so
      // only one grid is materialised at a time. This avoids the ~2x peak (old
      // grid still live while a whole new grid is rebuilt) that crashes
      // memory-capped in-app browsers like Instagram on large collections.
      output.replaceChildren(...target.childNodes);
    });

    // Re-initialize sort listeners after DOM replacement
    this.initializeSortListeners();
  }

  /**
   * Restore filter checkboxes to match current URL parameters
   * Called when dialog closes without applying filters
   */
  restoreFiltersFromURL() {
    const urlParams = new URLSearchParams(window.location.search);

    // Get all active filter values from URL (grouped by param name)
    const activeFilters = new Map();

    for (const [param, value] of urlParams.entries()) {
      if (param.startsWith('filter.')) {
        if (!activeFilters.has(param)) {
          activeFilters.set(param, new Set());
        }
        activeFilters.get(param).add(value);
      }
    }

    // Update all filter inputs to match URL
    this.querySelectorAll('input[name^="filter."]').forEach((input) => {
      const paramValues = activeFilters.get(input.name);
      input.checked = paramValues ? paramValues.has(input.value) : false;
    });

    // Restore the price slider to match the URL too.
    this.querySelector('[data-price-filter]')?.restoreFromURL?.();

    // Sync mobile/desktop via portal recall
    this.dispatchEvent(
      new CustomEvent('portal:recall', {
        bubbles: true,
      })
    );

    // Trigger count update without re-rendering product grid
    this.dispatchEvent(
      new CustomEvent('filter:count', {
        bubbles: true,
      })
    );
  }

  /**
   * Show error message to user
   */
  showErrorMessage(message) {
    // Create or find error container
    let errorContainer = this.section?.querySelector('[data-filter-error]');

    if (!errorContainer) {
      // Create error container if it doesn't exist
      errorContainer = document.createElement('div');
      errorContainer.setAttribute('data-filter-error', '');
      errorContainer.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in';
      errorContainer.setAttribute('role', 'alert');

      document.body.appendChild(errorContainer);
    }

    errorContainer.textContent = message;
    errorContainer.classList.remove('hidden');

    // Auto-hide after 5 seconds
    setTimeout(() => {
      errorContainer.classList.add('hidden');
    }, 5000);
  }

  disconnectedCallback() {
    // Cleanup
    clearTimeout(this.#debounceTimer);
    this.#abortController?.abort();

    // Remove portal recall event listener
    if (this.boundHandleRecall) {
      document.removeEventListener('portal:recall', this.boundHandleRecall);
    }

    // Remove error container if it exists
    const errorContainer = document.querySelector('[data-filter-error]');
    errorContainer?.remove();
  }
}

// Register custom element
if (!customElements.get('collection-filter')) {
  customElements.define('collection-filter', CollectionFilter);
}

// Also import and register other filter components
import './filter-item.js';
import './filter-clear.js';
import '../scripts/filter-search.js';
import '../scripts/price-filter.js';
